import { BlockNoteEditor, BlockSchema, Block } from '@packages/blocknote-core'
import { DragHandleMenuItem } from '../DragHandleMenuItem'
import { DragHandleMenuProps } from '../DragHandleMenu'
import { ReactNode } from 'react'
import iconsData from '../../../FormattingToolbar/components/FontIcons'

// Type for scheduling options
export type ScheduleOption = 'custom' | 'today' | 'tomorrow'

// eslint-disable-next-line unicorn/prevent-abbreviations -- Already defined in the type
export const ScheduleButton = <BSchema extends BlockSchema>(props: {
  itemProps: DragHandleMenuProps<BSchema> & { children: ReactNode }
  editor: BlockNoteEditor<BSchema>
  option?: ScheduleOption
}) => {
  const { option = 'custom' } = props
  const handleScheduleButtonClick = () => {
    let dateToSchedule = ''

    // Determine which date to use
    if (option === 'today') {
      const today = new Date()
      dateToSchedule = today.toISOString().split('T')[0]
    } else if (option === 'tomorrow') {
      // Get tomorrow's date in YYYY-MM-DD format
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      dateToSchedule = tomorrow.toISOString().split('T')[0]
    }

    // If we have a date to schedule for, add it
    if (dateToSchedule) {
      addScheduleText(props.itemProps.block, props.editor, dateToSchedule)
    } else {
      // Otherwise just add the '>' character (original behavior)
      addEmptyScheduleMarker(props.itemProps.block, props.editor)
    }

    props.itemProps.closeMenu()
  }

  // Get the appropriate icon based on the option
  const getIcon = () => {
    switch (props.option) {
      case 'today': {
        return iconsData.today()
      }
      case 'tomorrow': {
        return iconsData.tomorrow()
      }
      default: {
        return iconsData.schedule()
      }
    }
  }

  // Only show shortcut for the custom option
  const shortcut = option === 'custom' ? '>date' : undefined

  return (
    <DragHandleMenuItem
      icon={getIcon()}
      onClick={handleScheduleButtonClick}
      closeMenu={props.itemProps.closeMenu}
      // eslint-disable-next-line react/no-children-prop
      children={props.itemProps.children}
      shortcut={shortcut}
    />
  )
}

// Function to remove existing schedule text from a block
function removeExistingScheduleText<BSchema extends BlockSchema>(
  block: Block<BSchema>,
  editor: BlockNoteEditor<BSchema>
): Block<BSchema> {
  const existingContent = [...block.content]
  let contentChanged = false

  // Go through each content item and look for '>YYYY-MM-DD' or '>today' patterns
  for (let index = 0; index < existingContent.length; index++) {
    const item = existingContent[index]
    if ('text' in item) {
      // Matches: ">YYYY-MM-DD", ">today", ">tomorrow"
      // Exclude matches when preceded by backticks (e.g., `>today`)
      const scheduleDateRegex = /(?<=^|[^\\`])(\s|^)>((today|tomorrow)|\d{4}-\d{2}-\d{2})/g

      // Prüfe, ob das Muster gefunden wurde
      const hasSchedulePattern = scheduleDateRegex.test(item.text)

      // Zurücksetzen der RegExp wegen des test()-Aufrufs
      scheduleDateRegex.lastIndex = 0

      if (hasSchedulePattern) {
        // Remove the schedule text from the content item
        const newText = item.text.replaceAll(scheduleDateRegex, '$1')

        if (newText.trim() === '') {
          // If there's nothing left in the text after removal, remove the entire content item
          existingContent.splice(index, 1)
          index-- // Adjust index as we removed an item
        } else {
          // Otherwise update the text
          existingContent[index] = {
            ...item,
            text: newText,
          }
        }
        contentChanged = true
      }
    }
  }

  // If we found and removed any schedule text, update the block
  if (contentChanged) {
    const updatedBlock = { ...block, content: existingContent }
    editor.updateBlock(block, updatedBlock)
    return editor.getBlock(block.id) ?? updatedBlock
  }

  return block
}

// Helper function to add schedule text to a block
function addScheduleText<BSchema extends BlockSchema>(
  block: Block<BSchema>,
  editor: BlockNoteEditor<BSchema>,
  dateString: string
): Block<BSchema> {
  // First remove any existing schedule text
  const updatedBlock = removeExistingScheduleText(block, editor)

  // Set the cursor into the block
  if (typeof editor._tiptapEditor.commands.focus === 'function') {
    editor._tiptapEditor.commands.focus()
  }

  if (typeof editor.setTextCursorPosition === 'function') {
    editor.setTextCursorPosition(updatedBlock, 'end')
  }

  const existingContent = [...updatedBlock.content]

  // Check if the last block ends with a space, then don't add an extra space
  const lastContentItem = existingContent.at(-1)
  const textToAdd =
    lastContentItem &&
    'text' in lastContentItem &&
    lastContentItem.text.endsWith(' ')
      ? `>${dateString}`
      : ` >${dateString}`

  existingContent.push({
    attrs: {},
    styles: {},
    text: textToAdd,
    type: 'text',
  })

  const finalBlock = { ...updatedBlock, content: existingContent }
  editor.updateBlock(updatedBlock, finalBlock)

  if (typeof editor._tiptapEditor.commands.focus === 'function') {
    editor._tiptapEditor.commands.focus()
  }

  if (typeof editor.setTextCursorPosition === 'function') {
    editor.setTextCursorPosition(updatedBlock, 'end')
  }

  return editor.getBlock(block.id) ?? finalBlock
}

// Helper function to add just the '>' character (original behavior)
function addEmptyScheduleMarker<BSchema extends BlockSchema>(
  block: Block<BSchema>,
  editor: BlockNoteEditor<BSchema>
): Block<BSchema> {
  // First remove any existing schedule text
  const updatedBlock = removeExistingScheduleText(block, editor)

  // Set the cursor into the block
  editor._tiptapEditor.commands.focus()
  editor.setTextCursorPosition(updatedBlock, 'end')

  const existingContent = [...updatedBlock.content]

  // Check if the last block ends with a space, then don't add an extra space
  const lastContentItem = existingContent.at(-1)
  const textToAdd =
    lastContentItem &&
    'text' in lastContentItem &&
    lastContentItem.text.endsWith(' ')
      ? '>'
      : ' >'

  existingContent.push({
    attrs: {},
    styles: {},
    text: textToAdd,
    type: 'text',
  })

  const finalBlock = { ...updatedBlock, content: existingContent }
  editor.updateBlock(updatedBlock, finalBlock)

  editor._tiptapEditor.commands.focus()
  editor.setTextCursorPosition(updatedBlock, 'end')

  return editor.getBlock(block.id) ?? finalBlock
}
